import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/cn.ts';
import { springs } from '../motion/springs.ts';

export const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 select-none whitespace-nowrap font-semibold transition-colors disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none',
  {
    variants: {
      variant: {
        primary: 'bg-tint text-tint-fg hover:opacity-90',
        /** the rare purple moment (plan generation, upgrade); everything else is monochrome */
        accent: 'bg-brand text-brand-fg shadow-[var(--m-shadow-glow-brand)] hover:brightness-110',
        secondary: 'glass-1 text-text-primary hover:bg-[color-mix(in_srgb,var(--m-glass-1-fill),white_4%)]',
        ghost: 'bg-transparent text-text-primary hover:bg-glass-1',
        outline: 'border border-border-strong bg-transparent text-text-primary hover:bg-glass-1',
        danger: 'bg-status-cancelled text-white hover:brightness-110',
        soft: 'bg-tint-soft text-tint hover:brightness-110',
        /** Liquid Glass (regular) — floating controls over content/maps */
        glass: 'lg lg-press text-text-primary',
        /** Liquid Glass (clear) — over rich imagery only */
        'glass-clear': 'lg-clear lg-press text-white',
        /** tinted glass: the single prominent action on a glass surface */
        prominent: 'lg lg-tint lg-press ',
      },
      size: {
        sm: 'h-9 px-4 text-caption rounded-pill',
        md: 'h-12 px-5 text-body rounded-pill',
        lg: 'h-14 px-6 text-body-lg rounded-pill',
        icon: 'h-11 w-11 rounded-full p-0',
        'icon-sm': 'h-9 w-9 rounded-full p-0',
        'icon-lg': 'h-14 w-14 rounded-full p-0',
      },
      full: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', full: false },
  },
);

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  children?: ReactNode;
}

const MotionButton = motion.button;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, full, loading, leading, trailing, children, disabled, ...rest },
  ref,
) {
  return (
    <MotionButton
      ref={ref}
      whileTap={disabled || loading ? undefined : { scale: 0.97 }}
      transition={springs.snappy}
      className={cn(buttonVariants({ variant, size, full }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...(rest as object)}
    >
      {loading ? <Loader2 className="animate-spin" size={18} aria-hidden /> : leading}
      {children}
      {trailing}
    </MotionButton>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'leading' | 'trailing' | 'children' | 'size'> {
  label: string;
  size?: 'md' | 'sm' | 'lg';
  children: ReactNode;
}

/** Icon-only button with a required accessible label. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({ label, size = 'md', variant = 'ghost', children, ...rest }, ref) {
  return (
    <Button ref={ref} aria-label={label} title={label} variant={variant} size={size === 'sm' ? 'icon-sm' : size === 'lg' ? 'icon-lg' : 'icon'} {...rest}>
      {children}
    </Button>
  );
});
